<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.setting', 2), to: '/settings' },
      { label: 'Communications' },
      { label: 'Templates', to: '/settings/communication-templates' }
    ]"
  >
    <template #right>
      <Button
        v-if="$can('manage_communication_settings')"
        label="Add New"
        severity="primary"
        icon="plus"
        router-link="/settings/communication-templates/new"
      />
    </template>
  </PageHeader>

  <Card>
    <TableBasic
      bordered
      row-hover
    >
      <thead>
        <tr>
          <th>Name</th>
          <th>Type</th>
          <th>Used For</th>
          <th>Category</th>
          <th>Subject</th>
          <th>Default</th>
          <th>Dealerships</th>
          <th v-if="$can('manage_communication_settings')"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="templatesRequest.isLoading.value && !templates.length">
          <td
            class="text-tertiary"
            :colspan="$can('manage_communication_settings') ? 8 : 7"
          >
            Loading templates...
          </td>
        </tr>
        <tr v-else-if="!templates.length">
          <td
            class="text-tertiary"
            :colspan="$can('manage_communication_settings') ? 8 : 7"
          >
            No templates found.
          </td>
        </tr>
        <tr
          v-for="template in templates"
          :key="template.id"
        >
          <td>
            <RouterLink
              class="link"
              :to="`/settings/communication-templates/${template.id}`"
            >
              {{ template.name || 'Untitled template' }}
            </RouterLink>
          </td>
          <td>
            <Chip size="sm">
              {{ formatTemplateType(template.type) }}
            </Chip>
          </td>
          <td>{{ formatTemplateUsage(template.usedFor) }}</td>
          <td>{{ template.category || '—' }}</td>
          <td>
            <div
              class="max-w-[18rem] truncate"
              :title="template.subject || '—'"
            >
              {{ template.subject || '—' }}
            </div>
          </td>
          <td>
            <Chip
              :severity="template.isDefault ? 'success' : 'secondary'"
              size="sm"
            >
              {{ template.isDefault ? 'Yes' : 'No' }}
            </Chip>
          </td>
          <td>
            <ChipList
              v-if="template.dealerships.length"
              :items="getTemplateDealershipChipItems(template.dealerships)"
              :max-shown="2"
              :overflow-chip-props="{ severity: 'secondary', size: 'sm' }"
            />
            <span
              v-else
              class="text-secondary"
            >
              Global
            </span>
          </td>
          <td v-if="$can('manage_communication_settings')">
            <Dropdown>
              <template #triggerContent>
                <Button
                  icon="ellipsis-vertical"
                  plain
                  severity="secondary"
                />
              </template>
              <DropdownItem :route="`/settings/communication-templates/${template.id}`">
                <div class="flex items-center">
                  <Icon
                    type="pencil"
                    fa-style="fas"
                    class="mr-2"
                  />
                  Edit
                </div>
              </DropdownItem>
              <DropdownItem @click="deleteTemplate(template)">
                <div class="flex items-center">
                  <Icon
                    type="trash"
                    fa-style="fas"
                    class="mr-2"
                  />
                  Delete
                </div>
              </DropdownItem>
            </Dropdown>
          </td>
        </tr>
      </tbody>
    </TableBasic>
  </Card>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { RouterLink } from 'vue-router'

import Swal from 'sweetalert2'

// components
import Button from '@/components/button/Button.vue'
import Card from '@/components/card/Card.vue'
import Chip from '@/components/chip/Chip.vue'
import ChipList from '@/components/chip/ChipList.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import DropdownItem from '@/components/dropdown/DropdownItem.vue'
import Icon from '@/components/icon/Icon.vue'
import PageHeader from '@/components/page/PageHeader.vue'
import TableBasic from '@/components/tables/TableBasic.vue'

// composables
import useApiRequest from '@/composables/useApiRequest'

// types
import type { Dealership } from '@/types/domain/dealership'
import type {
  CommunicationTemplate,
  CommunicationTemplateType,
  CommunicationTemplateUsedFor
} from '@/types/domain/communicationTemplate'

// utilities
import { alertToast } from '@/utilities/notification'

/**
 * Chip props used to display dealership scope labels.
 */
interface DealershipChipItem {
  label: string
  severity: string
  size: string
}

const templates = ref<CommunicationTemplate[]>([])
const dealerships = ref<Dealership[]>([])

const templatesRequest = useApiRequest()
const dealershipsRequest = useApiRequest()
const deleteTemplateRequest = useApiRequest()

/**
 * Loads the full communication templates list for the table view.
 */
async function fetchTemplates(): Promise<void> {
  try {
    const response = await templatesRequest.send<CommunicationTemplate[]>({
      endpoint: '/v1/comms/templates',
      method: 'GET'
    })

    if (response.success && response.data) {
      templates.value = response.data
      return
    }

    alertToast('Error', response.message || 'Failed to fetch templates', 'error')
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch templates'
    alertToast('Error', message, 'error')
  }
}

/**
 * Loads dealerships so template scopes can be rendered in the table.
 */
async function fetchDealerships(): Promise<void> {
  try {
    const response = await dealershipsRequest.send<Dealership[]>({
      endpoint: '/v1/dealerships',
      method: 'GET',
      params: {
        showAll: true,
        showDisabled: true
      }
    })

    if (response.success && response.data) {
      dealerships.value = response.data
      return
    }

    alertToast('Error', response.message || 'Failed to fetch dealerships', 'error')
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch dealerships'
    alertToast('Error', message, 'error')
  }
}

/**
 * Deletes a template after the user confirms the destructive action.
 */
async function deleteTemplate(template: CommunicationTemplate): Promise<void> {
  const confirmation = await Swal.fire({
    title: 'Delete template?',
    text: `This will permanently remove "${template.name || 'this template'}".`,
    icon: 'warning',
    reverseButtons: true,
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Delete template',
    confirmButtonColor: 'var(--bg-error-solid)',
    cancelButtonText: 'Cancel'
  })

  if (!confirmation.isConfirmed) {
    return
  }

  try {
    const response = await deleteTemplateRequest.send({
      endpoint: `/v1/comms/templates/${template.id}`,
      method: 'DELETE'
    })

    if (!response.success) {
      alertToast('Error', response.message || 'Failed to delete template', 'error')
      return
    }

    alertToast('Success!', 'Template deleted', 'success')
    await fetchTemplates()
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to delete template'
    alertToast('Error', message, 'error')
  }
}

/**
 * Formats template purpose values for table display.
 */
function formatTemplateUsage(usedFor: CommunicationTemplateUsedFor | null): string {
  if (usedFor === 'marketing') {
    return 'Marketing'
  }

  if (usedFor === 'communication') {
    return 'Communication'
  }

  return '—'
}

/**
 * Formats template channel values for table display.
 */
function formatTemplateType(type: CommunicationTemplateType | null): string {
  if (type === 'sms') {
    return 'SMS'
  }

  if (type === 'email') {
    return 'Email'
  }

  return '—'
}

/**
 * Resolves dealership ids into chip items for scoped template display.
 */
function getTemplateDealershipChipItems(dealershipIds: number[]): DealershipChipItem[] {
  return dealershipIds.map(dealershipId => {
    const dealership = dealerships.value.find(item => item.id === dealershipId)

    return {
      label: dealership?.displayName || dealership?.name || `Dealership #${dealershipId}`,
      severity: 'secondary',
      size: 'sm'
    }
  })
}

void fetchDealerships()
void fetchTemplates()
</script>
